/** @jsxImportSource @emotion/react */
import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom';
import * as Yup from 'yup'
import { Formik } from 'formik'

import DashboardContent from 'components/lab/atoms/DashboardContent'
import Button from 'components/atoms/Button'
import routes from 'components/lab/constants/routes'
import { FormValues } from 'components/lab/billingProcedures/BillingProceduresForm'
import {
  useUpdateBillingProcedureMutation,
  useGetBillingProcedureQuery,
} from 'store/services/billing-procedures';
import BillingProceduresForm from 'components/lab/billingProcedures/BillingProceduresForm';
import Toast from 'components/atoms/Toast';

const procedureValidation = Yup.object().shape({
  name: Yup.string().required('Required'),
  cptCode: Yup.string().required('Required'),
  description: Yup.string(),
  shortName: Yup.string(),
  charge: Yup.number().required('Required'),
})

const EditBillingProcedure = () => {
  const { procedureId } = useParams();

  const navigate = useNavigate()
  const [isValid, setIsValid] = useState(false)
  const [isDirty, setIsDirty] = useState(false)
  const [submitCount, setSubmitCount] = useState(0)
  const [updateBillingProcedure] = useUpdateBillingProcedureMutation()
  const { data: billingProcedure } = useGetBillingProcedureQuery({ id: procedureId || '' })

  const initialValues: FormValues = {
    id: procedureId,
    name: billingProcedure?.name ?? '',
    shortName: billingProcedure?.shortName ?? '',
    description: billingProcedure?.description ?? '',
    cptCode: billingProcedure?.cptCode ?? '',
    charge: billingProcedure?.charge ? +billingProcedure.charge : undefined,
  }

  const onSubmit = async (values: FormValues) => {
    try{
      const response = await updateBillingProcedure(values);

      if (response && 'error' in response) {
        const error = response.error as {
          data?: { message: string; statusCode: number };
        };

        throw new Error(error?.data?.message ?? 'An unexpected error occurred.');
      }

      navigate(`${routes.index}${routes.billing.root}${routes.billing.procedures}`);
      Toast({ type: 'success', label: `Billing Procedure edited successfully!` })
    } catch (error) {
      Toast({ type: 'error', label: `${error instanceof Error ? error.message : error}` })
    }
  }

  return (
    <DashboardContent
      icon={''}
      title="PROCEDURES"
      subtitle={<>
        Edit Procedure
        <Button
          className="btn btn-primary position-absolute mr-3"
          css={{ top: '16px', right: '15px' }}
          type="submit"
          form="editBillingProcedure"
          disabled={!isDirty || !isValid}
          label="Save"
        />
      </>}
      content={
        <>
          <div className="card card-primary">
            <div className="card-body mt-3">

              {!isValid && submitCount > 0 && (
                <div className="row">
                  <div className="col-sm-12">
                    <div className="alert alert-warning" role="alert">
                      Procedure Information is missing. Please review the information below
                    </div>
                  </div>
                </div>
              )}

              <div className="row">
                <div className="col-sm-12">
                  <Formik
                    enableReinitialize
                    validateOnBlur
                    validationSchema={procedureValidation}
                    initialValues={initialValues}
                    onSubmit={onSubmit}>
                    {({
                        values,
                        dirty,
                        submitCount,
                        isValid,
                        handleSubmit,
                      }) => {
                      setIsValid(isValid)
                      setIsDirty(dirty)
                      setSubmitCount(submitCount)
                      return (
                        <form id="editBillingProcedure" className="px-2" onSubmit={handleSubmit}>
                          <BillingProceduresForm />
                        </form>
                      )
                    }}
                  </Formik>
                </div>
              </div>
            </div>
          </div>
        </>
      }
    />
  )
}

export default EditBillingProcedure
